/** Persisted runner-task tracking for Guardians audit launches (survives aniOS window unmount/remount). */

import type { SnapshotTaskStatus } from "@/services/api/data-hub/types";
import type { Audit, AuditStatus } from "@/services/api/guardiansv2/types";
import { isJsonObject, parseJsonArray, type JsonObject } from "../jsonValue";

export type GuardiansAuditLaunchKind = "full_mission" | "single" | "rerun" | "batch";

export interface GuardiansAuditLaunch {
  taskId: string;
  auditId?: string;
  missionId?: string;
  kind: GuardiansAuditLaunchKind;
  startedAt: number;
  /** Fixed batch size for ``full_mission`` runs (does not shrink as tasks finish). */
  batchTotal?: number;
}

/** Input for registering or updating one in-flight audit launch row. */
export interface RegisterGuardiansAuditLaunchInput {
  taskId: string;
  auditId?: string;
  missionId?: string;
  kind: GuardiansAuditLaunchKind;
  batchTotal?: number;
}

export function buildGuardiansAuditLaunch(
  input: RegisterGuardiansAuditLaunchInput,
  previous: GuardiansAuditLaunch | undefined,
  now: number
): GuardiansAuditLaunch {
  const launch: GuardiansAuditLaunch = {
    taskId: input.taskId,
    auditId: input.auditId,
    missionId: input.missionId,
    kind: input.kind,
    startedAt: previous?.startedAt ?? now,
  };
  const batchTotal = input.batchTotal ?? previous?.batchTotal;
  if (batchTotal !== undefined) {
    launch.batchTotal = batchTotal;
  }
  return launch;
}

/** Merge incoming launch rows into an existing list (keyed by ``taskId``). */
export function mergeLaunches(
  existing: GuardiansAuditLaunch[],
  incoming: RegisterGuardiansAuditLaunchInput[]
): GuardiansAuditLaunch[] {
  const byTask = new Map(existing.map((launch) => [launch.taskId, launch]));
  const now = Date.now();
  for (const row of incoming) {
    if (!row.taskId) continue;
    byTask.set(row.taskId, buildGuardiansAuditLaunch(row, byTask.get(row.taskId), now));
  }
  return [...byTask.values()];
}

export interface FullAuditProgress {
  active: boolean;
  finished: number;
  total: number;
  percent: number;
}

const STORAGE_KEY = "guardians-v2-audit-launches";
/** Drop stale launch rows on load (aniOS window may reopen hours later). */
export const LAUNCH_MAX_AGE_MS = 6 * 60 * 60 * 1000;

export const TERMINAL_AUDIT_STATUSES: ReadonlySet<AuditStatus> = new Set([
  "completed",
  "failed",
]);

const LAUNCH_KINDS: readonly GuardiansAuditLaunchKind[] = [
  "full_mission",
  "single",
  "rerun",
  "batch",
];

function isLaunchKind(value: string): value is GuardiansAuditLaunchKind {
  return (LAUNCH_KINDS as readonly string[]).includes(value);
}

function launchFromJsonObject(row: JsonObject): GuardiansAuditLaunch | null {
  const taskId = row.taskId;
  const kind = row.kind;
  const startedAt = row.startedAt;
  if (typeof taskId !== "string") return null;
  if (typeof kind !== "string" || !isLaunchKind(kind)) return null;
  if (typeof startedAt !== "number") return null;

  const launch: GuardiansAuditLaunch = { taskId, kind, startedAt };
  const auditId = row.auditId;
  if (typeof auditId === "string") launch.auditId = auditId;
  const missionId = row.missionId;
  if (typeof missionId === "string") launch.missionId = missionId;
  const batchTotal = row.batchTotal;
  if (typeof batchTotal === "number") launch.batchTotal = batchTotal;
  return launch;
}

export function loadPersistedLaunches(now = Date.now()): GuardiansAuditLaunch[] {
  if (typeof sessionStorage === "undefined") return [];
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return [];
  const parsed = parseJsonArray(raw);
  if (!parsed) return [];
  return parsed
    .map((item) => (isJsonObject(item) ? launchFromJsonObject(item) : null))
    .filter((launch): launch is GuardiansAuditLaunch => launch !== null)
    .filter((launch) => now - launch.startedAt < LAUNCH_MAX_AGE_MS);
}

export function savePersistedLaunches(launches: GuardiansAuditLaunch[]): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    if (launches.length === 0) {
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(launches));
  } catch {
    // ignore quota / private mode
  }
}

function missionFullLaunches(
  launches: GuardiansAuditLaunch[],
  missionId: string
): GuardiansAuditLaunch[] {
  return launches.filter(
    (launch) => launch.kind === "full_mission" && launch.missionId === missionId
  );
}

/** Progress for one in-flight full-mission audit batch. */
export function computeFullAuditProgress(
  launches: GuardiansAuditLaunch[],
  missionId: string,
  statuses: Record<string, SnapshotTaskStatus>,
  terminalStatuses: ReadonlySet<string>
): FullAuditProgress {
  const batch = missionFullLaunches(launches, missionId);
  if (batch.length === 0) {
    return { active: false, finished: 0, total: 0, percent: 0 };
  }

  const total = batch[0]?.batchTotal ?? batch.length;
  let finished = 0;
  let inProgressContribution = 0;

  for (const launch of batch) {
    const row = statuses[launch.taskId];
    if (!row) continue;
    if (terminalStatuses.has(row.status)) {
      finished += 1;
    } else {
      inProgressContribution += (row.progress ?? 0) / 100;
    }
  }

  const percent =
    total > 0
      ? Math.min(100, Math.round(((finished + inProgressContribution) / total) * 100))
      : 0;

  return { active: finished < total, finished, total, percent };
}

/** Progress for one single-audit runner task (alert issue, manual queue, rerun). */
export function computeSingleAuditProgress(
  launches: GuardiansAuditLaunch[],
  taskId: string,
  statuses: Record<string, SnapshotTaskStatus>,
  terminalStatuses: ReadonlySet<string>
): FullAuditProgress {
  const launch = launches.find((row) => row.taskId === taskId);
  if (!launch) {
    return { active: false, finished: 0, total: 0, percent: 0 };
  }
  const row = statuses[taskId];
  if (!row) {
    return { active: true, finished: 0, total: 1, percent: 0 };
  }
  if (terminalStatuses.has(row.status)) {
    return { active: false, finished: 1, total: 1, percent: 100 };
  }
  const percent = Math.min(100, Math.max(0, row.progress ?? 0));
  return { active: true, finished: 0, total: 1, percent };
}

/**
 * Batch progress from authoritative audit statuses (recoverable after aniOS window reopen).
 * Optionally refines in-flight percent via runner task stream snapshots.
 */
export function computeFullAuditProgressFromAudits(
  launches: GuardiansAuditLaunch[],
  scopeKey: string,
  audits: Audit[],
  statuses: Record<string, SnapshotTaskStatus> = {},
  terminalTaskStatuses: ReadonlySet<string> = new Set(),
  terminalAuditStatuses: ReadonlySet<AuditStatus> = TERMINAL_AUDIT_STATUSES
): FullAuditProgress {
  const batch = missionFullLaunches(launches, scopeKey);
  if (batch.length === 0) {
    return { active: false, finished: 0, total: 0, percent: 0 };
  }

  const total = batch[0]?.batchTotal ?? batch.length;
  const auditById = new Map(audits.map((audit) => [audit._id, audit]));
  let finished = 0;
  let inProgressContribution = 0;

  for (const launch of batch) {
    const audit = launch.auditId ? auditById.get(launch.auditId) : undefined;
    if (audit && terminalAuditStatuses.has(audit.status)) {
      finished += 1;
      continue;
    }

    const taskRow = statuses[launch.taskId];
    if (taskRow && terminalTaskStatuses.has(taskRow.status)) {
      finished += 1;
      continue;
    }

    if (taskRow && !terminalTaskStatuses.has(taskRow.status)) {
      inProgressContribution += (taskRow.progress ?? 0) / 100;
    } else if (audit?.status === "running") {
      inProgressContribution += 0.5;
    } else if (audit?.status === "queued") {
      inProgressContribution += 0.05;
    }
  }

  const percent =
    total > 0
      ? Math.min(100, Math.round(((finished + inProgressContribution) / total) * 100))
      : 0;

  return { active: finished < total, finished, total, percent };
}

/** Drop full-mission batches whose audits all reached a terminal status. */
export function pruneFinishedBatchesFromAudits(
  launches: GuardiansAuditLaunch[],
  audits: Audit[],
  terminalAuditStatuses: ReadonlySet<AuditStatus> = TERMINAL_AUDIT_STATUSES
): GuardiansAuditLaunch[] {
  const auditById = new Map(audits.map((audit) => [audit._id, audit]));
  const batches = new Map<string, GuardiansAuditLaunch[]>();

  for (const launch of launches) {
    if (launch.kind !== "full_mission" || !launch.missionId) continue;
    const rows = batches.get(launch.missionId) ?? [];
    rows.push(launch);
    batches.set(launch.missionId, rows);
  }

  const dropTaskIds = new Set<string>();
  for (const batch of batches.values()) {
    if (batch.length === 0) continue;
    const allTerminal = batch.every((launch) => {
      if (!launch.auditId) return false;
      const audit = auditById.get(launch.auditId);
      return audit != null && terminalAuditStatuses.has(audit.status);
    });
    if (allTerminal) {
      batch.forEach((launch) => dropTaskIds.add(launch.taskId));
    }
  }

  return launches.filter((launch) => !dropTaskIds.has(launch.taskId));
}

/** Task ids to drop once every row in a full-mission batch has reached a terminal status. */
export function fullMissionBatchTaskIdsToRemove(
  launches: GuardiansAuditLaunch[],
  missionId: string,
  statuses: Record<string, SnapshotTaskStatus>,
  terminalStatuses: ReadonlySet<string>
): string[] {
  const batch = missionFullLaunches(launches, missionId);
  if (batch.length === 0) return [];
  const allTerminal = batch.every((launch) => {
    const row = statuses[launch.taskId];
    return row != null && terminalStatuses.has(row.status);
  });
  return allTerminal ? batch.map((launch) => launch.taskId) : [];
}

export function buildFullAuditProgress(
  launches: GuardiansAuditLaunch[],
  missionId: string,
  finishedCount: number,
  percent: number
): FullAuditProgress {
  const missionLaunches = missionFullLaunches(launches, missionId);
  const total = missionLaunches[0]?.batchTotal ?? missionLaunches.length;
  return {
    active: missionLaunches.length > 0,
    finished: finishedCount,
    total,
    percent,
  };
}
